// Phase-A R7.9 — single source of truth for API-role → local-RoleId.
//
// Before R7.9 this function lived duplicated in
//   - apps/web/src/pages/auth/LoginPage.tsx (line 224, the R5 redesign)
//   - apps/web/src/pages/Auth.tsx           (line 22,  the pre-R5 legacy)
// Both copies mapped only 3 of 10 API roles (admin, instructor,
// student). The other 7 roles (parent, org, ta, content_manager,
// support, moderator, super_admin — all introduced in Phase 15 R7)
// silently fell through to `return "student"`, which routed those
// users to /progress + showed them the student navbar + student
// sidebar instead of their own.
//
// Gate A's measurement caught it on 2026-05-23:
//   - §5 role-routing 🔴 FAIL: 7 of 10 demo users land on /progress
//   - OWNER-FINDING-2: every role sees the same nav / sidebar after login
//
// Both symptoms collapse to this one upstream bug. R7.9 fixes it +
// extracts to this module so the duplicate-and-drift pattern that
// kept the bug alive through R5 can't recur. Anyone calling
// apiRoleToLocal MUST import it from here.

import type { RoleId } from "../role";

/**
 * Canonical privilege hierarchy, highest → lowest. The single source of
 * truth for BOTH `apiRoleToLocal` (primary = first match) and
 * `apiRolesToLocal` (ordered multi-role list).
 *
 *   super_admin > admin > content_manager > moderator > support >
 *   instructor > ta > org > parent > student
 *
 * The hierarchy is privilege-of-action, not seniority — `super_admin`
 * is highest because they cross tenants; `parent` and `org` are above
 * `student` because they manage learners; `student` is the safe
 * default when nothing matches.
 */
export const ROLE_PRIORITY: readonly RoleId[] = [
  "super_admin",
  "admin",
  // faculty_admin runs one faculty: below tenant-wide admin, above the
  // content/teaching roles (spec §2.2/§8.0).
  "faculty_admin",
  // finance_manager owns payroll/treasury/decrees — a scoped finance admin,
  // below faculty_admin, above content/teaching roles.
  "finance_manager",
  "content_manager",
  "moderator",
  "support",
  "instructor",
  "ta",
  // WELL-4 — «مشاور دانشگاه». Least-privilege wellness role; a staff member who
  // serves students' wellbeing. Above org/parent (it acts on the institution's
  // behalf for duty-of-care) but well below the academic/admin roles.
  "counselor",
  // S-CARD فاز A W1 — میزِ عضویت. مدیرِ برنامه بالای اپراتور (تعلیق/ابطال دارد)،
  // و هر دو زیرِ نقش‌های آموزشی/اداری: دامنه‌شان فقط رجیستریِ عضویت است.
  "membership_manager",
  "membership_operator",
  // S-ASSESS-W3a — کنسولِ ارزیاب. مدیرِ کانون بالای ارزیاب (نظارت/washup)،
  // هر دو دامنه‌شان محدود به کانونِ ارزیابی است؛ زیرِ نقش‌های آموزشی/اداری.
  "assessment_manager",
  "assessor",
  "org",
  "parent",
  "student",
];

/**
 * Map an API role list (e.g. `["instructor", "ta"]`) to a single local
 * `RoleId`. Highest privilege wins: a user with both `admin` +
 * `instructor` lands on the admin console (the more-privileged surface)
 * rather than the instructor one. See ROLE_PRIORITY for the order.
 *
 * Returns the SAME RoleId as `apiRolesToLocal(roles)[0]` for any input
 * with at least one known role — the two helpers share ROLE_PRIORITY so
 * the "primary" surface a user lands on equals the first entry of the
 * role-switcher list. `student` is the safe default when nothing matches.
 */
export const apiRoleToLocal = (roles: readonly string[] | undefined): RoleId => {
  if (!roles || roles.length === 0) return "student";
  const match = ROLE_PRIORITY.find((r) => roles.includes(r));
  // Unknown role → default to least-privileged. Better to under-grant
  // and let the user surface the problem than to over-grant.
  return match ?? "student";
};

/**
 * R-NAV-1 — Map an API role list to ALL recognised local RoleIds, ordered
 * by ROLE_PRIORITY (so `[0]` is the primary surface, identical to
 * `apiRoleToLocal`). Powers `RoleProvider.availableRoles` (the active-role
 * derivation + the multi-role switcher in the navbar, spec §3.1.6).
 *
 * Returns `[]` for an empty / undefined list (the anonymous case — no
 * role identity, spec §2.5). Unknown role names are dropped rather than
 * bucketed; a caller that needs a non-empty authenticated default applies
 * its own fallback (RoleProvider falls back to `student`, matching
 * apiRoleToLocal's safe default).
 */
export const apiRolesToLocal = (
  roles: readonly string[] | undefined,
): RoleId[] => {
  if (!roles || roles.length === 0) return [];
  return ROLE_PRIORITY.filter((r) => roles.includes(r));
};
